import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
	Dialog,
	TextField,
	IconButton,
	Box,
	Typography,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	Autocomplete,
	Chip,
} from '@mui/material';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import { ILoadingState } from 'src/components/common/types/loadingTypes';
import {
	ICreateSiteAccessFormData,
	ICreateSiteAccessModalProps,
} from 'src/features/site_access/domain/types';
import { CreateSiteAccessValidationSchema } from 'src/features/site_access/domain/validation';
import { SITE_ACCESS_LOADING_KEY } from 'src/features/site_access/store';
import { createSiteAccess } from 'src/features/site_access/store';
import { AppDispatch } from 'src/store';
import { snakeToTitleCase } from 'src/utils/formatter';

export const CreateSiteAccessModal: React.FC<ICreateSiteAccessModalProps> = ({
	siteSlug,
	open,
	onClose,
	onSubmit,
	users,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const isLoading = useSelector(
		(state: { loading: ILoadingState }) =>
			state.loading.loadingStates[SITE_ACCESS_LOADING_KEY],
	);
	const {
		control,
		watch,
		handleSubmit,
		resetField,
		setValue,
		formState: { errors },
		reset,
	} = useForm<ICreateSiteAccessFormData>({
		resolver: yupResolver(CreateSiteAccessValidationSchema),
	});

	const selectedType = watch('type');
	const selectedRoles = watch('roles');

	const handleCreateSiteAccess = (data: ICreateSiteAccessFormData) => {
		dispatch(
			createSiteAccess({
				request: data,
				siteSlug: siteSlug,
				onSuccess: () => {
					onSubmit?.(data);
					snackbar('Site access created successfully', 'success');
				},
			}),
		);
	};

	useEffect(() => {
		if (open) {
			reset();
		}
	}, [open, reset]);

	useEffect(() => {
		if (selectedType === 'admin') {
			resetField('roles');
		}

		if (
			selectedType === 'member' &&
			!selectedRoles?.includes('data_collector')
		) {
			setValue('can_add_patient', false);
		}
	}, [selectedType, selectedRoles, setValue, resetField]);

	if (!open) return null;

	const isCanAddPatientDisabled = () => {
		if (selectedType === 'admin') {
			return false;
		}
		if (selectedType === 'member') {
			return !selectedRoles?.includes('data_collector');
		}
		return false;
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth='sm'
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: '8px',
					padding: '24px',
					maxWidth: '610px',
					margin: '0 auto',
				},
			}}
			sx={{
				'& .MuiDialog-paper': {
					margin: '0 auto',
				},
				'& .MuiBackdrop-root': {
					backgroundColor: 'rgba(0, 0, 0, 0.5)',
				},
			}}
		>
			<Box sx={{ maxWidth: '552px', width: '100%', margin: '0 auto' }}>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start',
						width: '100%',
						minHeight: '49px',
					}}
				>
					<Typography
						variant='h6'
						component='h2'
						sx={{
							fontSize: '20px',
							fontWeight: 500,
							lineHeight: 1.5,
						}}
					>
						Add Site Access
					</Typography>
					<IconButton
						aria-label='close'
						onClick={onClose}
						sx={{
							padding: 0,
							'&:hover': {
								backgroundColor: 'transparent',
							},
						}}
					>
						<CloseIcon sx={{ fontSize: '25px' }} />
					</IconButton>
				</Box>

				<form onSubmit={handleSubmit(handleCreateSiteAccess)}>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
						}}
					>
						<Controller
							name='user_id'
							control={control}
							render={({
								field: { onChange, value, ...field },
							}) => (
								<FormControl
									fullWidth
									error={!!errors?.user_id}
								>
									<Autocomplete
										{...field}
										options={users}
										getOptionLabel={(option) =>
											`${option.first_name} ${option.last_name} - ${option.email}`
										}
										value={
											users.find(
												(user) => user.id === value,
											) || null
										}
										onChange={(_, newValue) => {
											onChange(newValue?.id || '');
										}}
										ListboxProps={{
											style: {
												maxHeight: '300px',
											},
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label='User'
												error={!!errors?.user_id}
												helperText={
													errors?.user_id?.message
												}
											/>
										)}
									/>
								</FormControl>
							)}
						/>
						<Controller
							name='type'
							control={control}
							render={({ field }) => (
								<FormControl fullWidth error={!!errors?.type}>
									<InputLabel>Type</InputLabel>
									<Select
										{...field}
										label='Type'
										error={!!errors?.type}
									>
										<MenuItem value='admin'>Admin</MenuItem>
										<MenuItem value='member'>
											Member
										</MenuItem>
									</Select>
									{errors?.type && (
										<Typography
											color='error'
											variant='caption'
											sx={{
												ml: 1.8,
												fontSize: '0.75rem',
											}}
										>
											{errors.type.message}
										</Typography>
									)}
								</FormControl>
							)}
						/>

						<Controller
							name='roles'
							control={control}
							disabled={selectedType === 'admin'}
							render={({ field }) => (
								<FormControl fullWidth error={!!errors?.roles}>
									<InputLabel>Roles</InputLabel>
									<Select
										label='Roles'
										multiple
										{...field}
										value={field.value || []}
										onChange={(e) =>
											field.onChange(e.target.value)
										}
										renderValue={(selected) => (
											<Box
												sx={{
													display: 'flex',
													flexWrap: 'wrap',
													gap: 0.5,
												}}
											>
												{(selected as string[]).map(
													(value) => (
														<Chip
															key={value}
															label={snakeToTitleCase(
																value,
															)}
															onMouseDown={(
																event,
															) => {
																event.stopPropagation();
															}}
															onDelete={() => {
																const newValue =
																	(
																		selected as string[]
																	).filter(
																		(
																			item,
																		) =>
																			item !==
																			value,
																	);
																field.onChange(
																	newValue,
																);
															}}
														/>
													),
												)}
											</Box>
										)}
									>
										<MenuItem value='data_collector'>
											Data Collector
										</MenuItem>
										<MenuItem value='annotator'>
											Annotator
										</MenuItem>
									</Select>
									{errors?.roles && (
										<Typography
											color='error'
											variant='caption'
											sx={{
												ml: 1.8,
												fontSize: '0.75rem',
											}}
										>
											{errors.roles.message}
										</Typography>
									)}
								</FormControl>
							)}
						/>

						<Controller
							name='can_add_patient'
							control={control}
							render={({ field }) => (
								<FormControl
									fullWidth
									error={!!errors?.can_add_patient}
								>
									<InputLabel>
										Allow user to create patients
									</InputLabel>
									<Select
										{...field}
										label='Allow user to create patients'
										error={!!errors?.can_add_patient}
										onChange={(e) =>
											field.onChange(
												e.target.value === 'true',
											)
										}
										disabled={isCanAddPatientDisabled()}
									>
										<MenuItem value='true'>
											Allow patient creation
										</MenuItem>
										<MenuItem value='false'>
											Disallow patient creation
										</MenuItem>
									</Select>
									{errors?.can_add_patient && (
										<Typography
											color='error'
											variant='caption'
											sx={{
												ml: 1.8,
												fontSize: '0.75rem',
											}}
										>
											{errors.can_add_patient.message}
										</Typography>
									)}
								</FormControl>
							)}
						/>
						<Controller
							name='initials'
							control={control}
							render={({ field }) => (
								<TextField
									placeholder='Initials'
									label='Initials'
									fullWidth
									size='medium'
									{...field}
									error={!!errors?.initials}
									helperText={errors?.initials?.message}
									variant='outlined'
								/>
							)}
						/>
						<PrimaryButton
							type='submit'
							isLoading={isLoading}
							sx={{
								width: '100%',
								marginTop: '8px',
							}}
						>
							CREATE
						</PrimaryButton>
					</Box>
				</form>
			</Box>
		</Dialog>
	);
};
